import {
    type MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import * as signalR from '@microsoft/signalr';

export type SignalRConnectionState =
    | 'Connected'
    | 'Disconnected'
    | 'Reconnecting';

export interface SignalRGroupSubscription<TCallbacks> {
    callbacks: TCallbacks;
    joined: boolean;
    joinPromise: Promise<void> | null;
}

interface UseSignalRGroupHubOptions<TCallbacks> {
    hubName: string;
    hubUrl: string;
    enabled?: boolean;
    accessTokenFactory: () =>
        | string
        | null
        | undefined
        | Promise<string | null | undefined>;
    withCredentials?: boolean;
    joinMethod: string;
    leaveMethod: string;
    registerHandlers: (
        connection: signalR.HubConnection,
        subscriptionsRef: MutableRefObject<
            Map<string, SignalRGroupSubscription<TCallbacks>>
        >
    ) => void;
    onCatchUpNeeded?: (
        groupId: string,
        callbacks: TCallbacks
    ) => void | Promise<void>;
}

interface UseSignalRGroupHubResult<TCallbacks> {
    connectionState: SignalRConnectionState;
    error: Error | null;
    subscribeToGroup: (groupId: string, callbacks: TCallbacks) => () => void;
    waitUntilJoined: (groupId: string) => Promise<void>;
    isGroupJoined: (groupId: string) => boolean;
}

const retryDelaysMs = [0, 2000, 10000, 30000, 60000];

const getRetryDelay = (retryCount: number) =>
    retryDelaysMs[Math.min(retryCount, retryDelaysMs.length - 1)];

const delay = (ms: number) =>
    new Promise<void>(resolve => {
        window.setTimeout(resolve, ms);
    });

const toError = (err: unknown) =>
    err instanceof Error ? err : new Error(String(err));

export function useSignalRGroupHub<TCallbacks>({
    hubName,
    hubUrl,
    enabled = true,
    accessTokenFactory,
    withCredentials,
    joinMethod,
    leaveMethod,
    registerHandlers,
    onCatchUpNeeded
}: UseSignalRGroupHubOptions<TCallbacks>): UseSignalRGroupHubResult<TCallbacks> {
    const [connectionState, setConnectionState] =
        useState<SignalRConnectionState>('Disconnected');
    const [error, setError] = useState<Error | null>(null);
    const [joinedVersion, setJoinedVersion] = useState(0);

    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const subscriptionsRef = useRef<
        Map<string, SignalRGroupSubscription<TCallbacks>>
    >(new Map());
    const activeRef = useRef(false);
    const startPromiseRef = useRef<Promise<void> | null>(null);

    const accessTokenFactoryRef = useRef(accessTokenFactory);
    const onCatchUpNeededRef = useRef(onCatchUpNeeded);

    useEffect(() => {
        accessTokenFactoryRef.current = accessTokenFactory;
    }, [accessTokenFactory]);

    useEffect(() => {
        onCatchUpNeededRef.current = onCatchUpNeeded;
    }, [onCatchUpNeeded]);

    const joinGroup = useCallback(
        async (
            groupId: string,
            subscription: SignalRGroupSubscription<TCallbacks>,
            catchUpAfterJoin: boolean
        ) => {
            const connection = connectionRef.current;
            if (!connection) {
                return;
            }

            if (subscription.joinPromise) {
                await subscription.joinPromise;
                return;
            }

            subscription.joinPromise = (async () => {
                await connection.invoke(joinMethod, groupId);

                const latestSubscription =
                    subscriptionsRef.current.get(groupId);
                if (!latestSubscription) {
                    return;
                }

                latestSubscription.joined = true;
                setJoinedVersion(version => version + 1);

                if (catchUpAfterJoin) {
                    await onCatchUpNeededRef.current?.(
                        groupId,
                        latestSubscription.callbacks
                    );
                }
            })().finally(() => {
                const latestSubscription =
                    subscriptionsRef.current.get(groupId);
                if (latestSubscription) {
                    latestSubscription.joinPromise = null;
                }
            });

            await subscription.joinPromise;
        },
        [joinMethod]
    );

    const joinPendingGroups = useCallback(
        async (catchUpAfterJoin: boolean) => {
            for (const [groupId, subscription] of subscriptionsRef.current) {
                if (!subscription.joined) {
                    try {
                        await joinGroup(
                            groupId,
                            subscription,
                            catchUpAfterJoin
                        );
                    } catch (err) {
                        console.error(
                            `[SignalR] Failed to join ${hubName} group ${groupId}:`,
                            err
                        );
                        setError(toError(err));
                    }
                }
            }
        },
        [hubName, joinGroup]
    );

    const startWithRetry = useCallback(
        async (
            connection: signalR.HubConnection,
            catchUpAfterStart: boolean
        ) => {
            let retryCount = 0;

            while (activeRef.current) {
                try {
                    await connection.start();
                    if (!activeRef.current) {
                        return;
                    }

                    setConnectionState('Connected');
                    setError(null);
                    await joinPendingGroups(catchUpAfterStart);
                    return;
                } catch (err) {
                    if (!activeRef.current) {
                        return;
                    }

                    const retryDelay = getRetryDelay(retryCount);
                    retryCount += 1;
                    setConnectionState('Disconnected');
                    setError(toError(err));
                    console.error(
                        `[SignalR] Failed to connect to ${hubName}; retrying in ${retryDelay}ms:`,
                        err
                    );

                    await delay(retryDelay);
                }
            }
        },
        [hubName, joinPendingGroups]
    );

    const ensureStarted = useCallback(
        (connection: signalR.HubConnection, catchUpAfterStart: boolean) => {
            if (!startPromiseRef.current) {
                startPromiseRef.current = startWithRetry(
                    connection,
                    catchUpAfterStart
                ).finally(() => {
                    startPromiseRef.current = null;
                });
            }

            return startPromiseRef.current;
        },
        [startWithRetry]
    );

    useEffect(() => {
        if (!enabled) {
            return;
        }

        activeRef.current = true;
        // Copy for the cleanup below — the ref's map is mutated, never reassigned,
        // so this is the same Map instance the effect body works with.
        const subscriptions = subscriptionsRef.current;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: async () =>
                    (await accessTokenFactoryRef.current()) ?? '',
                withCredentials
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext =>
                    getRetryDelay(retryContext.previousRetryCount)
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();

        registerHandlers(connection, subscriptionsRef);

        connection.onreconnecting(() => {
            setConnectionState('Reconnecting');
            setError(null);

            for (const subscription of subscriptionsRef.current.values()) {
                subscription.joined = false;
                subscription.joinPromise = null;
            }
            setJoinedVersion(version => version + 1);
        });

        connection.onreconnected(async () => {
            setConnectionState('Connected');
            setError(null);

            try {
                await joinPendingGroups(true);
            } catch (err) {
                setError(toError(err));
            }
        });

        connection.onclose(err => {
            setConnectionState('Disconnected');

            for (const subscription of subscriptionsRef.current.values()) {
                subscription.joined = false;
                subscription.joinPromise = null;
            }
            setJoinedVersion(version => version + 1);

            if (err) {
                setError(err);
            }

            if (activeRef.current) {
                void ensureStarted(connection, true);
            }
        });

        connectionRef.current = connection;
        void ensureStarted(connection, false);

        return () => {
            activeRef.current = false;
            startPromiseRef.current = null;
            connectionRef.current = null;
            subscriptions.clear();
            void connection.stop().catch(err => {
                console.error(`[SignalR] Failed to stop ${hubName}:`, err);
            });
        };
    }, [
        enabled,
        ensureStarted,
        hubName,
        hubUrl,
        joinPendingGroups,
        registerHandlers,
        withCredentials
    ]);

    const subscribeToGroup = useCallback(
        (groupId: string, callbacks: TCallbacks) => {
            const existingSubscription = subscriptionsRef.current.get(groupId);
            const subscription: SignalRGroupSubscription<TCallbacks> = {
                callbacks,
                joined: existingSubscription?.joined ?? false,
                joinPromise: existingSubscription?.joinPromise ?? null
            };

            subscriptionsRef.current.set(groupId, subscription);

            const connection = connectionRef.current;
            if (connection?.state === signalR.HubConnectionState.Connected) {
                void joinGroup(groupId, subscription, false);
            }

            return () => {
                const latestSubscription =
                    subscriptionsRef.current.get(groupId);
                if (!latestSubscription) {
                    return;
                }

                subscriptionsRef.current.delete(groupId);
                setJoinedVersion(version => version + 1);

                if (
                    connection?.state ===
                        signalR.HubConnectionState.Connected &&
                    latestSubscription.joined
                ) {
                    void connection.invoke(leaveMethod, groupId).catch(err => {
                        console.error(
                            `[SignalR] Failed to leave ${hubName} group ${groupId}:`,
                            err
                        );
                    });
                }
            };
        },
        [hubName, joinGroup, leaveMethod]
    );

    const waitUntilJoined = useCallback(
        async (groupId: string) => {
            const subscription = subscriptionsRef.current.get(groupId);
            if (!subscription) {
                throw new Error(
                    `${hubName} group ${groupId} is not subscribed.`
                );
            }

            if (subscription.joined) {
                return;
            }

            if (subscription.joinPromise) {
                await subscription.joinPromise;
                return;
            }

            const connection = connectionRef.current;
            if (!connection) {
                throw new Error(`${hubName} connection is not ready.`);
            }

            while (activeRef.current && !subscription.joined) {
                if (subscription.joinPromise) {
                    await subscription.joinPromise;
                    return;
                }

                if (connection.state === signalR.HubConnectionState.Connected) {
                    await joinGroup(groupId, subscription, false);
                    return;
                }

                if (startPromiseRef.current) {
                    await startPromiseRef.current;
                } else {
                    await delay(250);
                }
            }
        },
        [hubName, joinGroup]
    );

    const isGroupJoined = useCallback(
        (groupId: string) =>
            subscriptionsRef.current.get(groupId)?.joined === true,
        // eslint-disable-next-line react-hooks/exhaustive-deps -- joinedVersion intentionally invalidates this callback's identity so consumers re-read the mutable ref when join state changes
        [joinedVersion]
    );

    return {
        connectionState,
        error,
        subscribeToGroup,
        waitUntilJoined,
        isGroupJoined
    };
}
